import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Modal, IconButton, Divider, Grid, Paper,
    Skeleton, Chip, Alert, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, CircularProgress,
    Table, TableBody, TableCell, TableHead, TableRow, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaymentsIcon from '@mui/icons-material/Payments';
import RoomPreferencesIcon from '@mui/icons-material/RoomPreferences';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import BusinessIcon from '@mui/icons-material/Business';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useSelector } from 'react-redux';
import { ReservationService } from '../api/services/reservationService';
import { SettingsService } from '../api/services/settingsService';
import { useCurrency, formatCurrency } from '../utils/currency';
import { isUaeProperty, calculateDubaiHospitalityTax } from '../utils/dubaiHospitalityTax';
import ReservationStatusBadge from './ReservationStatusBadge';
import ApproveGovernmentModal from './ApproveGovernmentModal';

export default function GroupReservationDetailModal({ open, onClose, groupId, tenantId, setNotification, onClearanceApproved }) {
    const { currency } = useCurrency();
    const { tenants, user } = useSelector((state) => state.auth || {});
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [clearanceLoading, setClearanceLoading] = useState(false);
    const [govModalOpen, setGovModalOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [activeTaxes, setActiveTaxes] = useState([]);

    // Check MANAGE_CHECKIN permission
    const hasManageCheckin = React.useMemo(() => {
        if (!user) return true;
        const roleName = user.role?.name || user.roleName || '';
        if (roleName === 'Super Admin' || roleName === 'Admin') return true;

        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        const perms = rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') return p.key || p.name || p.code || '';
            return '';
        }).filter(Boolean);

        return perms.includes('*') || perms.includes('MANAGE_CHECKIN') || perms.includes('CAN_MANAGE_CHECKIN');
    }, [user]);

    useEffect(() => {
        if (!open || !tenantId) return;
        const fetchTaxes = async () => {
            try {
                const result = await SettingsService.getTaxes(tenantId, { noPagination: true });
                const list = Array.isArray(result) ? result : (result?.data || result?.taxes || []);
                setActiveTaxes(list.filter(t => t.status === 'Active'));
            } catch (err) {
                console.error('Failed to load taxes in GroupReservationDetailModal:', err);
            }
        };
        fetchTaxes();
    }, [open, tenantId]);

    const fetchDetail = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ReservationService.getGroupDetail(groupId, tenantId);
            setDetails(response?.data || response);
        } catch (err) {
            console.error('Failed to fetch group reservation details:', err);
            const msg = err?.response?.data?.message || err.message || 'Failed to load group details';
            setError(msg);
            setNotification?.({ open: true, message: msg, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !groupId) {
            setDetails(null);
            setError(null);
            return;
        }
        fetchDetail();
    }, [open, groupId, tenantId, setNotification]);

    const handleApproveRate = async () => {
        setActionLoading(true);
        try {
            await ReservationService.approveRateOverride(groupId, tenantId);
            setNotification?.({ open: true, message: 'Group rate override approved successfully.', severity: 'success' });
            fetchDetail();
        } catch (err) {
            console.error('Failed to approve group rate:', err);
            setNotification?.({ open: true, message: err?.response?.data?.message || 'Failed to approve rate override.', severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectRate = async () => {
        if (!rejectionReason.trim()) {
            setNotification?.({ open: true, message: 'Rejection reason is required.', severity: 'warning' });
            return;
        }
        setActionLoading(true);
        try {
            await ReservationService.rejectRateOverride(groupId, { reason: rejectionReason }, tenantId);
            setNotification?.({ open: true, message: 'Group rate override rejected successfully.', severity: 'success' });
            setRejectDialogOpen(false);
            setRejectionReason('');
            fetchDetail();
        } catch (err) {
            console.error('Failed to reject group rate:', err);
            setNotification?.({ open: true, message: err?.response?.data?.message || 'Failed to reject rate override.', severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const tenant = (tenants || []).find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const isUae = currency === 'AED' || isUaeProperty(tenant) || isUaeProperty(details?.hotel);

    const handleApproveGovernmentClearance = async (payload = {}) => {
        if (!groupId) return;
        setClearanceLoading(true);
        try {
            const body = {
                approvalReference: payload.approvalReference || 'DET-2026-98124',
                notes: payload.notes || 'Verified and cleared with Dubai Police / DET portal'
            };
            await ReservationService.approveGovernmentClearance(groupId, tenantId, body);
            setNotification?.({
                open: true,
                message: 'Government clearance approved successfully. Group reservation is now Confirmed.',
                severity: 'success'
            });
            await fetchDetail();
            if (onClearanceApproved) {
                onClearanceApproved(groupId);
            }
        } catch (error) {
            console.error('Failed to approve group government clearance:', error);
            const status = error?.response?.status;
            const msg = status === 403
                ? "Permission denied: You do not have the required 'MANAGE_CHECKIN' permission to approve government status."
                : (error?.response?.data?.message || 'Failed to approve government clearance.');
            setNotification?.({
                open: true,
                message: msg,
                severity: 'error'
            });
        } finally {
            setClearanceLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const getNights = (res) => {
        if (res?.nights !== undefined) return res.nights;
        if (res?.checkIn && res?.checkOut) {
            const diffTime = Math.abs(new Date(res.checkOut) - new Date(res.checkIn));
            return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
        }
        return 1;
    };

    const getAllFolioItems = (resData) => {
        if (!resData) return [];
        if (Array.isArray(resData.folio)) {
            return resData.folio.flatMap(f => (f?.items || []).map(item => ({
                ...item,
                folioId: f?.id,
                reservationRoomId: f?.reservationRoomId || item.reservationRoomId,
                folioStatus: f?.status
            })));
        }
        if (resData.folio?.items && Array.isArray(resData.folio.items)) {
            return resData.folio.items;
        }
        if (Array.isArray(resData.groupFinancialSummary?.folios)) {
            return resData.groupFinancialSummary.folios.flatMap(f => (f?.items || []).map(item => ({
                ...item,
                folioId: f?.id,
                reservationRoomId: f?.reservationRoomId || item.reservationRoomId,
                folioStatus: f?.status
            })));
        }
        return Array.isArray(resData.folioCharges) ? resData.folioCharges : [];
    };

    const getFolioPaidAmount = (resData) => {
        if (!resData) return 0;
        if (resData.groupFinancialSummary?.paidAmount !== undefined && Number(resData.groupFinancialSummary.paidAmount) > 0) {
            return Number(resData.groupFinancialSummary.paidAmount);
        }
        if (Array.isArray(resData.folio)) {
            const sumPaid = resData.folio.reduce((s, f) => s + Number(f?.paidAmount || 0), 0);
            if (sumPaid > 0) return sumPaid;
        }
        if (resData.folio?.paidAmount !== undefined && Number(resData.folio.paidAmount) > 0) {
            return Number(resData.folio.paidAmount);
        }
        if (resData.financials?.totalPaid !== undefined && Number(resData.financials.totalPaid) > 0) {
            return Number(resData.financials.totalPaid);
        }
        return Number(resData.totalAdvancePaid || resData.paidAmount || resData.payment?.paidAmount || 0);
    };

    const getAdvancePaid = (resData) => {
        if (!resData) return 0;
        const directAdv = Number(resData.totalAdvancePaid || resData.advancePaid || resData.advanceAmount || resData.billing?.advancePaid || resData.payment?.advancePaid || 0);
        if (directAdv > 0) return directAdv;
        const items = getAllFolioItems(resData);
        const depositSum = items
            .filter(i => !i.isVoided && (i.type === 'Deposit' || i.category === 'Deposit' || (i.description && (i.description.toLowerCase().includes('deposit') || i.description.toLowerCase().includes('advance')))))
            .reduce((s, i) => s + Number(i.amount || 0), 0);
        return depositSum;
    };

    const hasExtraBed = (resData) => {
        if (!resData) return false;
        if (resData.extraBed || resData.hasExtraBed || resData.extra_bed) return true;
        const allocations = resData.childReservations || resData.reservations || resData.rooms || [];
        if (allocations.some(r => r.extraBed || r.hasExtraBed || r.extra_bed)) return true;
        const folioItems = getAllFolioItems(resData);
        return folioItems.some(i =>
            !i.isVoided &&
            (i.category === 'extra_bed' || i.category === 'Extra Bed' || (i.description && i.description.toLowerCase().includes('extra bed')) || (i.item && i.item.toLowerCase().includes('extra bed')))
        );
    };

    const hasRoomExtraBed = (r, resData) => {
        if (!r) return false;
        if (r.extraBed || r.hasExtraBed || r.extra_bed) return true;
        const folioItems = getAllFolioItems(resData);
        return folioItems.some(i =>
            !i.isVoided &&
            (i.category === 'extra_bed' || i.category === 'Extra Bed' || (i.description && i.description.toLowerCase().includes('extra bed')) || (i.item && i.item.toLowerCase().includes('extra bed'))) &&
            (!i.reservationRoomId || i.reservationRoomId === r.id || i.reservationRoomId === r.roomId)
        );
    };

    const handleModalClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        if (onClose) onClose();
    };

    return (
        <>
        <Modal open={open} onClose={handleModalClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '96%', md: '94%', lg: '92%', xl: 1400 },
                maxWidth: 1440,
                bgcolor: '#f8fafc', borderRadius: 3,
                boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
                maxHeight: '94vh', overflowY: 'auto', display: 'flex', flexDirection: 'column',
                border: '1px solid #e2e8f0'
            }}>
                {/* Premium Gradient Header */}
                <Box sx={{
                    p: { xs: 2.5, sm: 3 },
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: 'rgba(59, 130, 246, 0.15)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <GroupsIcon sx={{ fontSize: 32, color: '#60a5fa' }} />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                                <Typography variant="h5" fontWeight="800" sx={{ letterSpacing: '-0.02em', color: '#f8fafc' }}>
                                    {details?.groupName ? `Group: ${details.groupName}` : 'Group Reservation'}
                                </Typography>
                                {!loading && (
                                    <ReservationStatusBadge
                                        reservation={details}
                                        isUae={isUae}
                                        direction="row"
                                        size="medium"
                                    />
                                )}
                                {!loading && (details?.pendingApproval || (details?.rateOverrides || []).some(ro => ro.status === 'Pending')) && (
                                    <Chip
                                        label="Rate Override Pending"
                                        size="small"
                                        sx={{
                                            fontWeight: '700',
                                            height: 24,
                                            fontSize: '0.75rem',
                                            bgcolor: 'rgba(245, 158, 11, 0.2)',
                                            color: '#fbbf24',
                                            border: '1px solid rgba(245, 158, 11, 0.3)'
                                        }}
                                    />
                                )}
                                {!loading && (details?.isFinalRateOverride || (details?.rateOverrides || []).some(ro => ro.status === 'Active' || ro.status === 'Approved')) && (
                                    <Chip
                                        label="Rate Override Active"
                                        size="small"
                                        sx={{
                                            fontWeight: '700',
                                            height: 24,
                                            fontSize: '0.75rem',
                                            bgcolor: 'rgba(59, 130, 246, 0.2)',
                                            color: '#93c5fd',
                                            border: '1px solid rgba(59, 130, 246, 0.3)'
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                                Booking ID: <Box component="span" sx={{ color: '#e2e8f0', fontWeight: 700 }}>{details?.bookingId || details?.id || groupId}</Box>
                                {details?.billingMode && (
                                    <Box component="span" sx={{ ml: 2, color: '#cbd5e1' }}>
                                        • Billing: <Box component="span" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{details.billingMode === 'individual' ? 'Individual Room Folios' : 'Master Bill'}</Box>
                                    </Box>
                                )}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {!loading && isUae && (details?.governmentStatus === 'Pending' || (!details?.governmentStatus && details?.status === 'Pending')) && (
                            <Tooltip title={!hasManageCheckin ? "Requires MANAGE_CHECKIN permission" : "Approve government clearance with Dubai Police / DET"}>
                                <span>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setGovModalOpen(true)}
                                        disabled={actionLoading || clearanceLoading || !hasManageCheckin}
                                        startIcon={clearanceLoading ? <CircularProgress size={16} color="inherit" /> : <VerifiedUserIcon fontSize="small" />}
                                        sx={{ fontWeight: 'bold', textTransform: 'none', px: 2, borderRadius: 1.5 }}
                                    >
                                        Approve Government Status
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {!loading && (details?.pendingApproval || (details?.rateOverrides || []).some(ro => ro.status === 'Pending')) && (
                            <Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleApproveRate}
                                    disabled={actionLoading}
                                    sx={{ fontWeight: 'bold', textTransform: 'none', px: 2, borderRadius: 1.5 }}
                                >
                                    Approve Rate
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setRejectDialogOpen(true)}
                                    disabled={actionLoading}
                                    sx={{ fontWeight: 'bold', textTransform: 'none', px: 2, borderRadius: 1.5, bgcolor: 'rgba(239,68,68,0.1)' }}
                                >
                                    Reject Rate
                                </Button>
                            </Box>
                        )}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#94a3b8',
                                bgcolor: 'rgba(255,255,255,0.05)',
                                '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.15)' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Modal Content Body */}
                <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflowY: 'auto' }}>
                    {isUae && (details?.governmentStatus === 'Pending' || (!details?.governmentStatus && details?.status === 'Pending')) && !loading && (
                        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                            <strong>Government Approval Required:</strong> In accordance with Dubai / UAE Tourism (DET) and local hospitality regulations, this group booking is pending regulatory verification before check-in can proceed. Authorized staff can approve status once verified.
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
                    )}

                    {loading ? (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2.5, mb: 3 }} />
                                <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2.5 }} />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2.5, mb: 3 }} />
                                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2.5 }} />
                            </Grid>
                        </Grid>
                    ) : details ? (
                        <Grid container spacing={3}>

                            {/* LEFT COLUMN: Metadata & Schedule */}
                            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                                {/* 1. Group & Organizer Profile Card */}
                                <Paper sx={{
                                    p: 2.5, borderRadius: 2.5,
                                    bgcolor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                    transition: 'all 0.2s ease-in-out'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                        <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#eff6ff', color: '#2563eb' }}>
                                            <PersonIcon fontSize="small" />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                            Group & Organizer Profile
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">GROUP NAME</Typography>
                                            <Typography variant="body2" fontWeight="700" color="#0f172a" sx={{ mt: 0.25 }}>
                                                {details.groupName || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">BOOKING SOURCE</Typography>
                                            <Chip
                                                size="small"
                                                icon={<BusinessIcon sx={{ fontSize: '0.9rem !important' }} />}
                                                label={details.bookingSourceDetails?.name ? (details.bookingSourceDetails.type && details.bookingSourceDetails.type !== details.bookingSourceDetails.name ? `${details.bookingSourceDetails.name} (${details.bookingSourceDetails.type})` : details.bookingSourceDetails.name) : (details.bookingSource || 'Corporate')}
                                                sx={{ mt: 0.25, fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', fontSize: '0.75rem' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">ORGANIZER / PRIMARY GUEST</Typography>
                                            <Typography variant="body1" fontWeight="800" color="#1e293b" sx={{ mt: 0.25 }}>
                                                {details.guest?.name || details.organizerName || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#64748b', mb: 0.25 }}>
                                                <PhoneIcon sx={{ fontSize: 14 }} />
                                                <Typography variant="caption" fontWeight={600}>PHONE</Typography>
                                            </Box>
                                            <Typography variant="body2" fontWeight="600" color="#334155">
                                                {details.guest?.phone || details.organizerPhone || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#64748b', mb: 0.25 }}>
                                                <EmailIcon sx={{ fontSize: 14 }} />
                                                <Typography variant="caption" fontWeight={600}>EMAIL</Typography>
                                            </Box>
                                            <Typography variant="body2" fontWeight="500" color="#334155" sx={{ wordBreak: 'break-all' }}>
                                                {details.guest?.email || details.organizerEmail || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        {details.guest?.address && (
                                            <Grid item xs={12}>
                                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0', mt: 0.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', mb: 0.5 }}>
                                                        <LocationOnIcon sx={{ fontSize: 14 }} />
                                                        <Typography variant="caption" fontWeight={700}>ADDRESS</Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {[details.guest.address, details.guest.city, details.guest.state, details.guest.country].filter(Boolean).join(', ')}
                                                        {details.guest.zip ? ` - ${details.guest.zip}` : ''}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}

                                        {details.guest?.idType && (
                                            <Grid item xs={12}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                    <BadgeIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>ID PROOF</Typography>
                                                        <Typography variant="caption" fontWeight={700} display="block" color="#0f172a">
                                                            {details.guest.idType}: {details.guest.idProof || 'Verified'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>

                                {/* 2. Stay Schedule & Policies Card */}
                                <Paper sx={{
                                    p: 2.5, borderRadius: 2.5,
                                    bgcolor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                        <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#eff6ff', color: '#2563eb' }}>
                                            <CalendarMonthIcon fontSize="small" />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                            Stay Schedule & Settings
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />

                                    {/* Stay Timeline Callout */}
                                    <Box sx={{
                                        p: 2, borderRadius: 2, bgcolor: '#f8fafc',
                                        border: '1px solid #e2e8f0', mb: 2,
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                    }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>CHECK-IN</Typography>
                                            <Typography variant="body2" fontWeight="800" color="#0f172a">
                                                {formatDate(details.checkIn || details.startDate)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <Chip
                                                label={`${getNights(details)} Night${getNights(details) > 1 ? 's' : ''}`}
                                                size="small"
                                                sx={{ fontWeight: 700, bgcolor: '#e0e7ff', color: '#4338ca', height: 22, fontSize: '0.7rem' }}
                                            />
                                            <ArrowForwardIcon sx={{ fontSize: 16, color: '#94a3b8', mt: 0.5 }} />
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>CHECK-OUT</Typography>
                                            <Typography variant="body2" fontWeight="800" color="#0f172a">
                                                {formatDate(details.checkOut || details.endDate)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">PAYMENT STATUS</Typography>
                                            <Chip
                                                label={details.paymentStatus || 'Due'}
                                                size="small"
                                                color={details.paymentStatus === 'Paid' ? 'success' : 'warning'}
                                                variant="outlined"
                                                sx={{ mt: 0.5, fontWeight: 'bold' }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">PAYMENT METHOD</Typography>
                                            <Typography variant="body2" fontWeight="700" color="#1e293b" sx={{ mt: 0.5 }}>
                                                {details.paymentMethod || details.payment?.paymentMethod || (details.modificationHistory && details.modificationHistory[0]?.paymentMethod) || 'Not Provided'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">BILLING MODE</Typography>
                                            <Typography variant="body2" fontWeight="700" color="#1e293b" sx={{ mt: 0.25, textTransform: 'capitalize' }}>
                                                {details.billingMode === 'individual' ? 'Individual Room Folios' : 'Master Bill'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">EXTRA BED</Typography>
                                            <Chip
                                                label={hasExtraBed(details) ? "Assigned (Yes)" : "None"}
                                                size="small"
                                                color={hasExtraBed(details) ? "primary" : "default"}
                                                variant={hasExtraBed(details) ? "filled" : "outlined"}
                                                sx={{ mt: 0.25, fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* RIGHT COLUMN: Financial Ledger & Room Breakdown */}
                            <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                                {/* 3. Centralized Financial Summary Ledger */}
                                <Paper sx={{
                                    p: 2.5, borderRadius: 2.5,
                                    bgcolor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#eff6ff', color: '#2563eb' }}>
                                                <PaymentsIcon fontSize="small" />
                                            </Box>
                                            <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                                Centralized Financial Ledger
                                            </Typography>
                                        </Box>
                                        <Chip label="Group Financial Summary" size="small" sx={{ bgcolor: '#f1f5f9', fontWeight: 700, color: '#475569', fontSize: '0.7rem' }} />
                                    </Box>
                                    <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />

                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        {(() => {
                                            const formatINR = (val) => {
                                                const num = Number(val || 0);
                                                return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                            };

                                            const allocations = details.childReservations || details.reservations || details.rooms || [];
                                            const hasRoomsArray = allocations.length > 0;
                                            const roomsBaseSum = hasRoomsArray ? allocations.reduce((s, r) => s + Number(r.roomTotal !== undefined ? r.roomTotal : 0), 0) : 0;
                                            const roomsDiscountSum = hasRoomsArray ? allocations.reduce((s, r) => s + Number(r.discounts !== undefined ? r.discounts : 0), 0) : 0;
                                            const roomsNetTaxableSum = hasRoomsArray ? allocations.reduce((s, r) => s + Number(r.netTaxableValue !== undefined ? r.netTaxableValue : 0), 0) : 0;
                                            const roomsTaxSum = hasRoomsArray ? allocations.reduce((s, r) => s + Number(r.taxTotal !== undefined ? r.taxTotal : 0), 0) : 0;

                                            // 1. Original Room Base
                                            const roomBase = Number(roomsBaseSum > 0 ? roomsBaseSum : (details.billing?.roomSubtotal || details.billing?.subtotal || (Number(details.totalAmount || 0) - Number(details.taxAmount || 0) + Number(details.discounts || 0))));

                                            // 2. Less: Final Rate Override (Pre-Tax)
                                            const rateOverride = Number(details.discounts !== undefined && Number(details.discounts) > 0 ? details.discounts : (roomsDiscountSum > 0 ? roomsDiscountSum : (details.billing?.overrideOrDiscount || details.billing?.discountAmount || 0)));

                                            // 3. Net Taxable Value
                                            const netTaxableVal = Number(details.netTaxableValue !== undefined ? details.netTaxableValue : (roomsNetTaxableSum > 0 ? roomsNetTaxableSum : (details.billing?.netTaxableValue !== undefined && Number(details.billing?.overrideOrDiscount || 0) === rateOverride ? details.billing.netTaxableValue : Math.max(0, roomBase - rateOverride))));

                                            // 4. Statutory Taxes / Fees Breakdown
                                            let uaeTaxBreakdown = null;
                                            let taxAccrued = 0;
                                            let gstPct = null;
                                            let taxLabel = '';

                                            if (isUae) {
                                                const nights = (details.checkIn && details.checkOut)
                                                    ? Math.max(1, Math.round((new Date(details.checkOut) - new Date(details.checkIn)) / (1000 * 60 * 60 * 24)))
                                                    : (Number(details.nights) || 1);
                                                const bedroomsCount = (allocations && allocations.length > 0) ? allocations.length : 1;
                                                const computedUae = calculateDubaiHospitalityTax({
                                                    netBaseAmount: netTaxableVal,
                                                    nights,
                                                    bedroomsCount,
                                                    rating: details.hotel?.rating || tenant?.rating || 4,
                                                    hotelType: details.hotel?.type || tenant?.type || '',
                                                    taxes: activeTaxes
                                                });

                                                const serviceChargeAmount = Number(details.serviceCharge !== undefined ? details.serviceCharge : (details.billing?.serviceCharge !== undefined ? details.billing.serviceCharge : (details.pricing?.serviceCharge !== undefined ? details.pricing.serviceCharge : computedUae.serviceCharge)));
                                                const municipalityFeeAmount = Number(details.municipalityFee !== undefined ? details.municipalityFee : (details.billing?.municipalityFee !== undefined ? details.billing.municipalityFee : (details.pricing?.municipalityFee !== undefined ? details.pricing.municipalityFee : computedUae.municipalityFee)));
                                                const vatAmount = Number(details.vat !== undefined ? details.vat : (details.billing?.vat !== undefined ? details.billing.vat : (details.pricing?.vat !== undefined ? details.pricing.vat : computedUae.vat)));
                                                const tourismDirhamAmount = Number(details.tourismDirham !== undefined ? details.tourismDirham : (details.billing?.tourismDirham !== undefined ? details.billing.tourismDirham : (details.pricing?.tourismDirham !== undefined ? details.pricing.tourismDirham : computedUae.tourismDirham)));
                                                const tdRate = Number(details.tourismDirhamRate || details.billing?.tourismDirhamRate || computedUae.tourismDirhamRate || 15);

                                                taxAccrued = serviceChargeAmount + municipalityFeeAmount + vatAmount + tourismDirhamAmount;
                                                uaeTaxBreakdown = {
                                                    serviceChargeAmount,
                                                    serviceChargePct: computedUae.serviceChargePct || 10,
                                                    municipalityFeeAmount,
                                                    municipalityFeePct: computedUae.municipalityFeePct || 7,
                                                    vatAmount,
                                                    vatPct: computedUae.vatPct || 5,
                                                    tourismDirhamAmount,
                                                    tourismDirhamRate: tdRate
                                                };
                                            } else {
                                                taxAccrued = Number(details.taxAmount !== undefined && Number(details.taxAmount) > 0 ? details.taxAmount : (details.taxTotal !== undefined ? details.taxTotal : (details.billing?.estimatedTax !== undefined ? details.billing.estimatedTax : roomsTaxSum)));
                                                const roomWithTax = hasRoomsArray ? allocations.find(r => Number(r.taxTotal !== undefined ? r.taxTotal : (r.roomTaxAmount || 0)) > 0 && Number(r.netTaxableValue !== undefined ? r.netTaxableValue : (r.roomTotal || 0)) > 0) : null;
                                                const roomGstPct = roomWithTax ? Math.round((Number(roomWithTax.taxTotal !== undefined ? roomWithTax.taxTotal : roomWithTax.roomTaxAmount) / Number(roomWithTax.netTaxableValue !== undefined ? roomWithTax.netTaxableValue : roomWithTax.roomTotal)) * 100) : null;
                                                gstPct = (details.taxExempt || details.taxPercentage === 0)
                                                    ? null
                                                    : (details.gstPercentage !== undefined && Number(details.gstPercentage) > 0
                                                        ? Number(details.gstPercentage)
                                                        : (details.billing?.gstPercentage !== undefined && Number(details.billing.gstPercentage) > 0
                                                            ? Number(details.billing.gstPercentage)
                                                            : (roomGstPct !== null
                                                                ? roomGstPct
                                                                : (netTaxableVal > 0 && taxAccrued > 0 ? Math.round((taxAccrued / netTaxableVal) * 100) : null))));
                                                taxLabel = details.taxExempt ? 'Total Tax Accrued (Exempt)' : (gstPct !== null ? `Total Tax Accrued (${gstPct}%)` : 'Total Tax Accrued (5%)');
                                            }

                                            // 5. Subtotal (Room & Tax)
                                            const subtotalRoomAndTax = netTaxableVal + taxAccrued;

                                            // 6. Other Folio Charges (Undiscounted)
                                            const folioItems = getAllFolioItems(details);
                                            const folioIncidentals = folioItems
                                                .filter(i => {
                                                    if (i.isVoided) return false;
                                                    const type = (i.type || '').toLowerCase();
                                                    const cat = (i.category || '').toLowerCase();
                                                    const desc = (i.description || '').toLowerCase();
                                                    if (['room charge', 'tax', 'payment', 'settlement', 'refund', 'deposit', 'adjust'].includes(type)) return false;
                                                    if (['room charge', 'tax', 'payment', 'settlement', 'refund', 'deposit', 'discount', 'tax reversal'].includes(cat)) return false;
                                                    if (desc.includes('nightly room charge') || desc.includes('gst (cgst+sgst) for room')) return false;
                                                    return true;
                                                })
                                                .reduce((s, i) => s + Number(i.amount || 0), 0);
                                            const otherFolioCharges = Number(folioIncidentals > 0 ? folioIncidentals : (details.additionalCharges || details.billing?.additionalCharges || details.financials?.incidentalCharges || 0)) + Number(details.cancellationFee || 0);

                                            // 7. Pre-Discount Folio Total
                                            const preDiscountFolioTotal = subtotalRoomAndTax + otherFolioCharges;

                                            // 8. Less: Flat Checkout Discount
                                            const folioAdjustDiscounts = folioItems
                                                .filter(i => !i.isVoided && ((i.type === 'Adjust' && (i.category === 'Discount' || i.category === 'Tax Reversal' || (i.description && i.description.toLowerCase().includes('discount')))) || (i.category === 'Discount' && Number(i.amount) < 0)))
                                                .reduce((s, i) => s + Math.abs(Number(i.amount || 0)), 0);
                                            const flatCheckoutDiscount = Number(details.flatDiscount !== undefined && Number(details.flatDiscount) > 0 ? details.flatDiscount : (details.billing?.flatDiscount !== undefined && Number(details.billing.flatDiscount) > 0 ? details.billing.flatDiscount : (details.checkoutDiscount || details.flatCheckoutDiscount || details.billing?.checkoutDiscount || details.financials?.checkoutDiscount || details.billing?.flatCheckoutDiscount || folioAdjustDiscounts || 0)));

                                            // 9. Final Total Charges
                                            const finalTotalCharges = isUae ? preDiscountFolioTotal : Math.max(0, preDiscountFolioTotal - flatCheckoutDiscount);

                                            // 10. Less: Advance Paid
                                            const advancePaid = getAdvancePaid(details);
                                            const totalPaid = getFolioPaidAmount(details);

                                            // 11. Updated Balance Due
                                            const rawBalance = finalTotalCharges - (totalPaid > 0 ? totalPaid : advancePaid);
                                            const balanceDue = (details.paymentStatus === 'Paid' || totalPaid >= finalTotalCharges) ? 0 : Math.max(0, rawBalance);
                                            const isRefund = rawBalance < 0;

                                            const renderRow = (label, value, isBold = false) => (
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    py: 1.25,
                                                    borderBottom: '1px solid #f1f5f9'
                                                }}>
                                                    <Typography sx={{
                                                        fontSize: isBold ? '0.875rem' : '0.845rem',
                                                        color: isBold ? '#0f172a' : '#334155',
                                                        fontWeight: isBold ? 700 : 400
                                                    }}>
                                                        {label}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: isBold ? '0.9rem' : '0.845rem',
                                                        color: isBold ? '#0f172a' : '#1e293b',
                                                        fontWeight: isBold ? 700 : 400
                                                    }}>
                                                        {value}
                                                    </Typography>
                                                </Box>
                                            );

                                            return (
                                                <>
                                                    {renderRow('Original Room Base', formatCurrency(roomBase, currency), false)}
                                                    {renderRow('Less: Final Rate Override (Pre-Tax)', `-${formatCurrency(rateOverride, currency)}`, false)}
                                                    {renderRow('Net Taxable Value', formatCurrency(netTaxableVal, currency), true)}
                                                    {isUae && uaeTaxBreakdown ? (
                                                        <>
                                                            {renderRow(`Service Charge (${uaeTaxBreakdown.serviceChargePct}%)`, formatCurrency(uaeTaxBreakdown.serviceChargeAmount, currency), false)}
                                                            {renderRow(`Dubai Municipality Fee (${uaeTaxBreakdown.municipalityFeePct}%)`, formatCurrency(uaeTaxBreakdown.municipalityFeeAmount, currency), false)}
                                                            {renderRow(`VAT (${uaeTaxBreakdown.vatPct}%)`, formatCurrency(uaeTaxBreakdown.vatAmount, currency), false)}
                                                            {renderRow(`Tourism Dirham (AED ${uaeTaxBreakdown.tourismDirhamRate} / night)`, formatCurrency(uaeTaxBreakdown.tourismDirhamAmount, currency), false)}
                                                        </>
                                                    ) : (
                                                        renderRow(taxLabel, formatCurrency(taxAccrued, currency), false)
                                                    )}
                                                    {renderRow('Subtotal (Room & Tax)', formatCurrency(subtotalRoomAndTax, currency), true)}
                                                    {renderRow('Other Folio Charges (Undiscounted)', formatCurrency(otherFolioCharges, currency), false)}
                                                    {!isUae && renderRow('Pre-Discount Folio Total', formatCurrency(preDiscountFolioTotal, currency), true)}
                                                    {!isUae && renderRow('Less: Flat Checkout Discount', `-${formatCurrency(flatCheckoutDiscount, currency)}`, false)}
                                                    {renderRow('Final Total Charges', formatCurrency(finalTotalCharges, currency), true)}
                                                    {renderRow('Less: Advance Paid', `-${formatCurrency(advancePaid, currency)}`, false)}
                                                    {renderRow('Updated Balance Due', `${isRefund ? '-' : ''}${formatCurrency(Math.abs(balanceDue), currency)}`, true)}
                                                    {/* Balance Due Callout Banner */}
                                                    <Box sx={{
                                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                        p: 2,
                                                        bgcolor: isRefund ? '#eff6ff' : (balanceDue === 0 ? '#f0fdf4' : '#fef2f2'),
                                                        borderRadius: 2,
                                                        border: `1px solid ${isRefund ? '#bfdbfe' : (balanceDue === 0 ? '#bbf7d0' : '#fecaca')}`,
                                                        mt: 1.5
                                                    }}>
                                                        <Box>
                                                            <Typography variant="caption" fontWeight="800" color={isRefund ? "#1e40af" : (balanceDue === 0 ? "#166534" : "#991b1b")}>
                                                                {isRefund ? 'REFUND DUE TO GUEST' : (balanceDue === 0 ? 'PAYMENT STATUS' : 'OUTSTANDING BALANCE DUE')}
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight="900" color={isRefund ? "#1d4ed8" : (balanceDue === 0 ? "#15803d" : "#dc2626")}>
                                                                {formatCurrency(Math.abs(balanceDue), currency)}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={isRefund ? "Refund" : (balanceDue === 0 ? "Settled" : "Payment Due")}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                bgcolor: isRefund ? '#dbeafe' : (balanceDue === 0 ? '#dcfce7' : '#fee2e2'),
                                                                color: isRefund ? '#1e40af' : (balanceDue === 0 ? '#166534' : '#991b1b')
                                                            }}
                                                        />
                                                    </Box>
                                                </>
                                            );
                                        })()}
                                    </Box>
                                </Paper>

                                {/* 4. Room Billing Breakdown Cards */}
                                {((details.childReservations && details.childReservations.length > 0) || (details.rooms && details.rooms.length > 0) || (details.billing?.roomBreakdown && details.billing.roomBreakdown.length > 0)) && (
                                    <Paper sx={{
                                        p: 2.5, borderRadius: 2.5,
                                        bgcolor: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#eff6ff', color: '#2563eb' }}>
                                                <ReceiptLongIcon fontSize="small" />
                                            </Box>
                                            <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                                Room Billing Breakdown
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />

                                        <Grid container spacing={2}>
                                            {(() => {
                                                const allocations = details.childReservations || details.reservations || details.rooms || details.billing?.roomBreakdown || [];
                                                return allocations.map((item, idx) => {
                                                    const billingItem = (details.billing?.roomBreakdown || [])[idx] || (details.billing?.roomBreakdown || []).find(b => b.roomId === item.roomId || b.roomId === item.id) || item;
                                                    const roomInfo = item.room ? item : (allocations.find(r => r.roomId === billingItem?.roomId || r.id === billingItem?.roomId) || item);

                                                    const roomName = roomInfo?.room?.roomNumber || roomInfo?.roomNumber || billingItem?.roomNumber || `Room ${idx + 1}`;
                                                    const roomTypeName = roomInfo?.roomType?.name || roomInfo?.room?.type || billingItem?.roomType?.name || 'Standard';
                                                    const nights = billingItem?.nights || (item.checkIn && item.checkOut ? Math.ceil(Math.abs(new Date(item.checkOut) - new Date(item.checkIn)) / (1000 * 60 * 60 * 24)) : 2);
                                                    const roomLabel = `Room #${roomName} (${roomTypeName}) • ${nights}N`;

                                                    const roomRate = Number(roomInfo?.roomTotal !== undefined ? roomInfo.roomTotal : (billingItem?.roomRate !== undefined ? billingItem.roomRate : billingItem?.roomTotal || 0));
                                                    const discount = Number(roomInfo?.discounts !== undefined ? roomInfo.discounts : (billingItem?.discountApplied || 0));
                                                    const netTaxable = Number(roomInfo?.netTaxableValue !== undefined ? roomInfo.netTaxableValue : (billingItem?.netTaxableValue !== undefined ? billingItem.netTaxableValue : (roomRate - discount)));
                                                    const taxAmt = Number(roomInfo?.taxTotal !== undefined ? roomInfo.taxTotal : (billingItem?.roomTaxAmount !== undefined ? billingItem.roomTaxAmount : (billingItem?.estimatedTax || 0)));
                                                    const finalTotal = Number(roomInfo?.calculatedTotal !== undefined ? roomInfo.calculatedTotal : (netTaxable + taxAmt));
                                                    const isRoomTaxExempt = Boolean(details.taxExempt || roomInfo?.taxExempt || billingItem?.taxExempt);
                                                    const gstPct = isRoomTaxExempt ? 0 : (billingItem?.gstPercentage !== undefined && billingItem.gstPercentage > 0 ? billingItem.gstPercentage : (netTaxable > 0 && taxAmt > 0 ? Math.round((taxAmt / netTaxable) * 100) : 0));

                                                    let resolvedRoomUae = null;
                                                    if (isUae && !isRoomTaxExempt && netTaxable > 0) {
                                                        const rawBreakdown = billingItem?.uaeBreakdown || calculateDubaiHospitalityTax({
                                                            netBaseAmount: netTaxable,
                                                            nights: nights || 1,
                                                            bedroomsCount: 1,
                                                            rating: details.hotel?.rating || tenant?.rating || 4,
                                                            hotelType: details.hotel?.type || tenant?.type || '',
                                                            taxes: activeTaxes
                                                        });

                                                        if (rawBreakdown) {
                                                            const scAmt = Number(billingItem?.serviceCharge !== undefined ? billingItem.serviceCharge : (rawBreakdown.serviceCharge !== undefined ? rawBreakdown.serviceCharge : (rawBreakdown.serviceChargeAmount || 0)));
                                                            const scRate = Number(rawBreakdown.serviceChargeRate || rawBreakdown.serviceChargePct || 10);
                                                            const mfAmt = Number(billingItem?.municipalityFee !== undefined ? billingItem.municipalityFee : (rawBreakdown.municipalityFee !== undefined ? rawBreakdown.municipalityFee : (rawBreakdown.municipalityFeeAmount || 0)));
                                                            const mfRate = Number(rawBreakdown.municipalityRate || rawBreakdown.municipalityFeePct || 7);
                                                            const vatAmt = Number(billingItem?.vat !== undefined ? billingItem.vat : (rawBreakdown.vat !== undefined ? rawBreakdown.vat : (rawBreakdown.vatAmount || 0)));
                                                            const vatRate = Number(rawBreakdown.vatRate || rawBreakdown.vatPct || 5);
                                                            let tdAmt = Number(billingItem?.tourismDirham !== undefined ? billingItem.tourismDirham : (rawBreakdown.tourismDirham !== undefined ? rawBreakdown.tourismDirham : (rawBreakdown.tourismDirhamAmount || 0)));
                                                            const tdRate = Number(rawBreakdown.tourismDirhamRate || 15);

                                                            if (taxAmt > 0 && Math.abs((scAmt + mfAmt + vatAmt + tdAmt) - taxAmt) > 0.05) {
                                                                const calcSumNoTd = scAmt + mfAmt + vatAmt;
                                                                if (taxAmt >= calcSumNoTd) {
                                                                    tdAmt = Number((taxAmt - calcSumNoTd).toFixed(2));
                                                                }
                                                            }

                                                            resolvedRoomUae = {
                                                                serviceCharge: scAmt,
                                                                serviceChargeRate: scRate,
                                                                municipalityFee: mfAmt,
                                                                municipalityRate: mfRate,
                                                                vat: vatAmt,
                                                                vatRate: vatRate,
                                                                tourismDirham: tdAmt,
                                                                tourismDirhamRate: tdRate
                                                            };
                                                        }
                                                    }

                                                    return (
                                                        <Grid item xs={12} sm={6} key={idx}>
                                                            <Box sx={{
                                                                p: 2, bgcolor: '#f8fafc', borderRadius: 2,
                                                                border: '1px solid #e2e8f0',
                                                                display: 'flex', flexDirection: 'column', height: '100%',
                                                                transition: 'box-shadow 0.2s',
                                                                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                                                            }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                                    <Typography variant="subtitle2" fontWeight="800" color="#0f172a">
                                                                        {roomLabel}
                                                                    </Typography>
                                                                    <Chip label={`Room ${idx + 1}`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                                                                </Box>

                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                                                    <Typography variant="caption" color="text.secondary">Room Rate</Typography>
                                                                    <Typography variant="caption" fontWeight="700">{formatCurrency(roomRate, currency)}</Typography>
                                                                </Box>
                                                                {discount > 0 && (
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                                                        <Typography variant="caption" color="success.main" fontWeight={600}>Discount Applied</Typography>
                                                                        <Typography variant="caption" fontWeight="700" color="success.main">-{formatCurrency(discount, currency)}</Typography>
                                                                    </Box>
                                                                )}
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                                                    <Typography variant="caption" color="text.secondary">Net Taxable Value</Typography>
                                                                    <Typography variant="caption" fontWeight="800">{formatCurrency(netTaxable, currency)}</Typography>
                                                                </Box>
                                                                {isUae && resolvedRoomUae ? (
                                                                    <Box sx={{ my: 1, p: 1, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                                Service Charge ({resolvedRoomUae.serviceChargeRate}%)
                                                                            </Typography>
                                                                            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                                {formatCurrency(resolvedRoomUae.serviceCharge, currency)}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                                Municipality Fee ({resolvedRoomUae.municipalityRate}%)
                                                                            </Typography>
                                                                            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                                {formatCurrency(resolvedRoomUae.municipalityFee, currency)}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                                UAE VAT ({resolvedRoomUae.vatRate}%)
                                                                            </Typography>
                                                                            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                                {formatCurrency(resolvedRoomUae.vat, currency)}
                                                                            </Typography>
                                                                        </Box>
                                                                        {resolvedRoomUae.tourismDirham > 0 && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                                    Tourism Dirham Fee
                                                                                </Typography>
                                                                                <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                                                    {formatCurrency(resolvedRoomUae.tourismDirham, currency)}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #cbd5e1' }}>
                                                                            <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ fontSize: '0.78rem' }}>
                                                                                Total Dubai Levies & Taxes
                                                                            </Typography>
                                                                            <Typography variant="caption" fontWeight="700" color="#2563eb" sx={{ fontSize: '0.78rem' }}>
                                                                                {formatCurrency(taxAmt, currency)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                ) : (
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Room Tax {isRoomTaxExempt ? '(Exempt)' : (isUae ? '(Taxes & VAT)' : (gstPct > 0 ? `(${gstPct}% GST)` : ''))}
                                                                        </Typography>
                                                                        <Typography variant="caption" fontWeight="700" color="#2563eb">{formatCurrency(taxAmt, currency)}</Typography>
                                                                    </Box>
                                                                )}

                                                                <Divider sx={{ my: 1.25, borderStyle: 'dashed', borderColor: '#cbd5e1' }} />

                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                                                    <Typography variant="body2" fontWeight="800" color="#1e293b">Room Total</Typography>
                                                                    <Typography variant="body1" fontWeight="900" color="#16a34a">{formatCurrency(finalTotal, currency)}</Typography>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    );
                                                });
                                            })()}
                                        </Grid>
                                    </Paper>
                                )}

                                {/* 5. Allocated Rooms & Complete Guest List */}
                                <Paper sx={{
                                    p: 2.5, borderRadius: 2.5,
                                    bgcolor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                        <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#eff6ff', color: '#2563eb' }}>
                                            <RoomPreferencesIcon fontSize="small" />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                            Allocated Rooms & Guest List
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />

                                    <Box sx={{ overflowX: 'auto' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Room</TableCell>
                                                    <TableCell>Room Type</TableCell>
                                                    <TableCell>Assigned Guest</TableCell>
                                                    <TableCell>Extra Bed</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(() => {
                                                    const allocations = details.childReservations || details.reservations || details.rooms || [];
                                                    const assigned = allocations.filter(r => r.room || r.roomId || r.roomNumber);
                                                    if (assigned.length === 0) {
                                                        return (
                                                            <TableRow>
                                                                <TableCell colSpan={5} sx={{ py: 3, textAlign: 'center', color: '#94a3b8' }}>
                                                                    No allocated rooms recorded.
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }
                                                    return assigned.map((res, idx) => {
                                                        const primaryGuest = res.guests?.find(g => g.isPrimary) || res.guests?.[0];
                                                        const guestName = primaryGuest?.name || res.guest?.name || res.guestName || 'TBD';
                                                        const roomName = res.room?.roomNumber || res.roomNumber || res.room?.name || res.roomId;
                                                        const roomTypeName = res.roomType?.name || res.room?.type || 'Standard';
                                                        const roomOverride = (details.rateOverrides || []).find(ro => ro.reservationRoomId === res.id || ro.reservationRoomId === res.roomId) || (res.overrides && res.overrides[0]) || res.rateOverride;
                                                        return (
                                                            <React.Fragment key={res.id || idx}>
                                                                <TableRow sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                                    <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <MeetingRoomIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                                                                            {roomName}
                                                                            {roomOverride?.type && (
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={roomOverride.type === 'Percentage' ? `${roomOverride.value || 20}% Off` : roomOverride.type}
                                                                                    color="primary"
                                                                                    variant="outlined"
                                                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell sx={{ color: '#475569', fontWeight: 600 }}>{roomTypeName}</TableCell>
                                                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{guestName}</TableCell>
                                                                    <TableCell>
                                                                        {hasRoomExtraBed(res, details) ? (
                                                                            <Chip size="small" label="Yes" color="success" sx={{ fontWeight: 800, height: 20, fontSize: '0.65rem' }} />
                                                                        ) : (
                                                                            <Chip size="small" label="No" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', color: '#94a3b8' }} />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={res.status || 'Confirmed'}
                                                                            size="small"
                                                                            color={res.status === 'Checked-in' ? 'success' : 'default'}
                                                                            sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem' }}
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                                {res.dailyRates && res.dailyRates.length > 0 && (
                                                                    <TableRow>
                                                                        <TableCell colSpan={5} sx={{ p: 0, pb: 1.5, px: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e2e8f0' }}>
                                                                            <Box sx={{ bgcolor: '#ffffff', p: 1.25, borderRadius: 1.5, border: '1px solid #e2e8f0', mt: 0.5 }}>
                                                                                <Typography variant="caption" fontWeight="800" color="#64748b" display="block" mb={0.75}>
                                                                                    NIGHTLY RATE BREAKDOWN
                                                                                </Typography>
                                                                                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                                                                                    {res.dailyRates.map((d, di) => (
                                                                                        <Box key={di} sx={{
                                                                                            minWidth: 100, p: 0.75, bgcolor: '#f8fafc',
                                                                                            border: '1px solid #cbd5e1', borderRadius: 1.25, textAlign: 'center'
                                                                                        }}>
                                                                                            <Typography variant="caption" display="block" color="#64748b" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                                                                                                {new Date(d.date).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}
                                                                                            </Typography>
                                                                                            <Typography variant="caption" display="block" fontWeight="800" color="#0f172a" sx={{ fontSize: '0.75rem' }}>
                                                                                                {formatCurrency(d.overrideRate !== undefined ? Number(d.overrideRate) : Number(d.baseRate), currency)}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    ))}
                                                                                </Box>
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })()}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Paper>

                                {/* 6. Inventory Allocation Status */}
                                {(() => {
                                    const unassignedList = details.unassignedGuests || [];
                                    const allocations = details.childReservations || details.reservations || details.rooms || [];
                                    const unassignedAllocations = allocations.filter(r => !r.room && !r.roomId && !r.roomNumber);
                                    const totalUnassigned = [...unassignedList, ...unassignedAllocations];

                                    if (totalUnassigned.length === 0) {
                                        return (
                                            <Paper sx={{
                                                p: 2, borderRadius: 2,
                                                bgcolor: '#f0fdf4',
                                                border: '1px solid #bbf7d0',
                                                display: 'flex', alignItems: 'center', gap: 1.5
                                            }}>
                                                <CheckCircleOutlineIcon sx={{ color: '#16a34a' }} />
                                                <Box>
                                                    <Typography variant="body2" fontWeight="800" color="#166534">
                                                        Inventory Allocation Complete
                                                    </Typography>
                                                    <Typography variant="caption" color="#15803d">
                                                        All group rooms and attending guests have been assigned to physical resort inventory.
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        );
                                    }

                                    return (
                                        <Paper sx={{
                                            p: 2.5, borderRadius: 2.5,
                                            bgcolor: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            borderLeft: '5px solid #ef4444'
                                        }}>
                                            <Typography variant="subtitle2" fontWeight="800" color="#991b1b" gutterBottom>
                                                Unallocated Inventory Exceptions ({totalUnassigned.length})
                                            </Typography>
                                            <Typography variant="caption" color="#b91c1c" display="block" mb={1.5}>
                                                The following guests require room/floor assignment:
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {totalUnassigned.map((res, idx) => {
                                                    const primaryGuest = res.guests?.find(g => g.isPrimary) || res.guests?.[0];
                                                    const guestName = res.name || primaryGuest?.name || res.guest?.name || res.guestName || 'Unnamed Guest';
                                                    return (
                                                        <Grid item xs={12} sm={6} key={res.id || idx}>
                                                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#ffffff', borderColor: '#fca5a5' }}>
                                                                <Typography variant="body2" fontWeight="bold">{guestName}</Typography>
                                                                <Typography variant="caption" color="text.secondary" display="block">ID: {res.id || 'Pending'}</Typography>
                                                                <Chip label="Assignment Required" size="small" color="error" sx={{ mt: 1, height: 20, fontSize: '0.65rem' }} />
                                                            </Paper>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Paper>
                                    );
                                })()}

                                {/* 7. Rate History Audit Trail */}
                                <Paper sx={{
                                    p: 2.5, borderRadius: 2.5,
                                    bgcolor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                        <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#eff6ff', color: '#2563eb' }}>
                                            <LocalOfferIcon fontSize="small" />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                            Rate History Audit Trail
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />

                                    {(details.rateOverrides || []).length > 0 ? (
                                        <Table size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Scope / Room</TableCell>
                                                    <TableCell>Type</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Original Rate</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Overridden Rate</TableCell>
                                                    <TableCell>Reason</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(details.rateOverrides || []).map((ro, i) => {
                                                    let displayReason = ro.reasonCategory || ro.reason || ro.overrideReason || 'Discount';
                                                    let remarks = '';
                                                    let parsedType = ro.overrideType === 'EntireStay' ? 'Entire Stay' : (ro.overrideType === 'SpecificDates' ? 'Specific Date' : (ro.overrideType || 'Stay Override'));
                                                    try {
                                                        if (ro.reasonText && ro.reasonText.startsWith('{')) {
                                                            const parsed = JSON.parse(ro.reasonText);
                                                            displayReason = parsed.reason || displayReason;
                                                            remarks = parsed.remarks || '';
                                                            if (parsed.type === 'Percentage') {
                                                                parsedType = `Percentage (${parsed.value}%)`;
                                                            } else if (parsed.type === 'FinalRateOverride') {
                                                                parsedType = 'Final Rate Override';
                                                            } else if (parsed.type) {
                                                                parsedType = parsed.type;
                                                            }
                                                        } else if (ro.reasonText) {
                                                            remarks = ro.reasonText;
                                                        }
                                                    } catch(e) {}

                                                    const allocations = details.childReservations || details.reservations || details.rooms || [];
                                                    const targetRoom = ro.reservationRoomId ? allocations.find(r => r.id === ro.reservationRoomId || r.roomId === ro.reservationRoomId) : null;
                                                    const targetScope = targetRoom ? `Room ${targetRoom.room?.roomNumber || targetRoom.roomNumber || 'Assigned'}` : 'Master Group';

                                                    return (
                                                        <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                            <TableCell sx={{ color: '#64748b', fontSize: '0.78rem', py: 1, whiteSpace: 'nowrap' }}>
                                                                {formatDate(ro.createdAt || ro.date || ro.timestamp)}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }}>
                                                                <Chip size="small" label={targetScope} variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
                                                                {ro.startDate && (
                                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                                                                        {formatDate(ro.startDate)}{ro.endDate && ro.endDate !== ro.startDate ? ` - ${formatDate(ro.endDate)}` : ''}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 1 }}>
                                                                <Typography variant="caption" fontWeight="700" color="#334155">{parsedType}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ color: '#64748b', py: 1, textDecoration: ro.overriddenRate ? 'line-through' : 'none', whiteSpace: 'nowrap' }}>
                                                                {ro.originalRate ? formatCurrency(Number(ro.originalRate), currency) : '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#2563eb', py: 1, whiteSpace: 'nowrap' }}>
                                                                {ro.overriddenRate ? formatCurrency(Number(ro.overriddenRate), currency) : '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 1 }}>
                                                                <Typography variant="caption" fontWeight="600" color="#0f172a">{displayReason}</Typography>
                                                                {remarks && <Typography variant="caption" color="text.secondary" display="block">{remarks}</Typography>}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={ro.status || 'Active'}
                                                                    color={ro.status === 'Approved' || ro.status === 'Active' ? 'success' : ro.status === 'Pending' ? 'warning' : ro.status === 'Rejected' ? 'error' : 'default'}
                                                                    sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem' }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1.5 }}>
                                            No rate overrides recorded for this group reservation.
                                        </Typography>
                                    )}
                                </Paper>

                                {/* 8. Modification Audit Trail */}
                                {(details.modificationHistory || []).length > 0 && (
                                    <Paper sx={{
                                        p: 2.5, borderRadius: 2.5,
                                        bgcolor: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#eff6ff', color: '#2563eb' }}>
                                                <HistoryEduIcon fontSize="small" />
                                            </Box>
                                            <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                                Modification Audit Trail
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {details.modificationHistory.map((m, i) => (
                                                <Box key={i} sx={{
                                                    p: 1.5, bgcolor: '#f8fafc', borderRadius: 2,
                                                    border: '1px solid #e2e8f0', borderLeft: '4px solid #3b82f6'
                                                }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                        <Typography variant="body2" fontWeight={800} color="#0f172a">
                                                            {m.action || m.event || 'Group Reservation Event'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(m.timestamp || m.createdAt).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    {m.groupName && <Typography variant="caption" color="#475569" display="block">Group Name: <strong>{m.groupName}</strong></Typography>}
                                                    {m.paymentMethod && <Typography variant="caption" color="#475569" display="block">Payment Method: <strong>{m.paymentMethod}</strong></Typography>}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Paper>
                                )}

                            </Grid>
                        </Grid>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">No group reservation details available.</Typography>
                        </Box>
                    )}
                </Box>

                {/* Modal Footer */}
                <Box sx={{ p: 2, px: 3, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={onClose} sx={{ minWidth: 100, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}>
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>

        {/* Rejection Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => !actionLoading && setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>Reject Group Rate Override</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please provide a reason for rejecting this group rate override request.
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Rejection Reason"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    disabled={actionLoading}
                    multiline
                    rows={3}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleRejectRate} color="error" variant="contained" disabled={actionLoading || !rejectionReason.trim()} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    {actionLoading ? <CircularProgress size={24} color="inherit" /> : 'Reject'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Government Approval Modal */}
        <ApproveGovernmentModal
            open={govModalOpen}
            onClose={() => setGovModalOpen(false)}
            reservation={details}
            tenantId={tenantId}
            onSuccess={() => {
                fetchDetail();
                if (onClearanceApproved) onClearanceApproved(groupId);
            }}
            setNotification={setNotification}
        />
        </>
    );
}
